import React, { useState, useEffect, Suspense, lazy, startTransition, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ClubExperienceContext from './contexts/ClubExperienceContext';
import Home from './components/Home';
import Landing from './components/Landing';
import Login from './components/Login';
import Navbar from './components/Navbar';
import BottomNav from './components/BottomNav';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import api from './config/api';
import NewUserSetup from './components/NewUserSetup';

const ClubList = lazy(() => import('./components/ClubList'));
const ClubDetail = lazy(() => import('./components/ClubDetail'));
const Recommendations = lazy(() => import('./components/Recommendations'));
const Profile = lazy(() => import('./components/Profile'));
const CreateGroup = lazy(() => import('./components/CreateGroup'));
const Friends = lazy(() => import('./components/Friends'));
const ExploreEvents = lazy(() => import('./components/ExploreEvents'));
const Hangouts = lazy(() => import('./components/Hangouts'));
const Reels = lazy(() => import('./components/Reels'));
const MapPage = lazy(() => import('./components/MapPage'));
const ActivityFeed = lazy(() => import('./components/ActivityFeed'));
const Tour = lazy(() => import('./components/Tour'));
const Privacy = lazy(() => import('./components/Privacy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const Blog = lazy(() => import('./components/Blog'));
const BlogArticle = lazy(() => import('./components/BlogArticle'));

function App() {
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [showXpModal, setShowXpModal] = useState(false);
  const [showNewUserSetup, setShowNewUserSetup] = useState(false);

  useEffect(() => {
    const storedSessionId = localStorage.getItem('sessionId');
    const storedUser = localStorage.getItem('user');
    
    if (storedSessionId && storedUser) {
      let parsedUser;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('sessionId');
        localStorage.removeItem('user');
        setIsLoading(false);
        return;
      }

      startTransition(() => {
        setSessionId(storedSessionId);
        setUser(parsedUser);
      });
      
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session verify timeout')), 4000)
      );
      Promise.race([api.get('/auth/me'), timeout])
        .then(res => {
          startTransition(() => {
            setUser(res.data);
          });
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch((err) => {
          console.error('Session verification failed:', err.message);
          if (err.response?.status === 401 || err.response?.status === 403) {
            localStorage.removeItem('sessionId');
            localStorage.removeItem('user');
            startTransition(() => {
              setUser(null);
              setSessionId(null);
            });
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleLogin = useCallback((userData, session) => {
    localStorage.setItem('sessionId', session);
    localStorage.setItem('user', JSON.stringify(userData));
    startTransition(() => {
      setUser(userData);
      setSessionId(session);
    });
    const hasCompletedSetup = localStorage.getItem('hasCompletedSetup') === 'true';
    if (!hasCompletedSetup && !userData?.school) {
      setShowNewUserSetup(true);
    } else {
      const hasSeenTour = localStorage.getItem('hasSeenTour') === 'true';
      if (!hasSeenTour) {
        startTransition(() => setShowTour(true));
      }
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    startTransition(() => {
      setUser(null);
      setSessionId(null);
    });
  }, []);

  const handleProfileUpdate = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] dark:bg-[#18181b]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-gray-300 dark:border-gray-800 border-t-gray-900 dark:border-t-white"></div>
          <p className="mt-4 text-gray-600 dark:text-white text-[17px]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-[#f5f5f7] text-gray-900 dark:bg-[#18181b] dark:text-[#e5e5e5]">
        {showTour && (
          <Tour user={user} onComplete={() => setShowTour(false)} />
        )}
        {showNewUserSetup && user && (
          <NewUserSetup
            user={user}
            sessionId={sessionId}
            onComplete={() => {
              setShowNewUserSetup(false);
              const stored = localStorage.getItem('user');
              if (stored) {
                try { setUser(JSON.parse(stored)); } catch { /* ignore */ }
              }
              const hasSeenTour = localStorage.getItem('hasSeenTour') === 'true';
              if (!hasSeenTour) startTransition(() => setShowTour(true));
            }}
          />
        )}
        {user ? (
          <ClubExperienceContext.Provider value={{ openModal: () => setShowXpModal(true) }}>
            <Navbar 
              user={user} 
              sessionId={sessionId}
              showXpModal={showXpModal}
              setShowXpModal={setShowXpModal}
              onLogout={handleLogout}
              onStartTour={() => {
                localStorage.removeItem('hasSeenTour');
                startTransition(() => setShowTour(true));
              }}
            />
            <div className="h-screen pt-14 pb-16 md:pb-0 overflow-hidden flex flex-col">
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900 dark:border-gray-700 dark:border-t-white"></div></div>}>
            <div className="flex-1 min-h-0 overflow-x-hidden flex flex-col overflow-y-auto">
            <Routes>
              <Route path="/" element={<Home user={user} />} />
              <Route path="/groups" element={<ClubList user={user} sessionId={sessionId} />} />
              <Route path="/groups/:id" element={<ClubDetail user={user} sessionId={sessionId} />} />
              <Route path="/clubs" element={<ClubList user={user} sessionId={sessionId} />} />
              <Route path="/clubs/:id" element={<ClubDetail user={user} sessionId={sessionId} />} />
              <Route path="/recommendations" element={<Recommendations user={user} sessionId={sessionId} />} />
              <Route path="/profile" element={<Profile user={user} sessionId={sessionId} onUpdate={handleProfileUpdate} onLogout={handleLogout} />} />
              <Route path="/create-group" element={<CreateGroup user={user} sessionId={sessionId} />} />
              <Route path="/friends" element={<Friends user={user} sessionId={sessionId} />} />
              <Route path="/events" element={<ExploreEvents user={user} sessionId={sessionId} />} />
              <Route path="/hangouts" element={<Hangouts user={user} sessionId={sessionId} />} />
              <Route path="/reels" element={<Reels user={user} sessionId={sessionId} />} />
              <Route path="/map" element={<MapPage user={user} />} />
              <Route path="/habitats" element={<Navigate to="/hangouts?tab=habitats" replace />} />
              <Route path="/activity" element={<ActivityFeed user={user} sessionId={sessionId} />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/article" element={<BlogArticle />} />
            </Routes>
            </div>
            </Suspense>
            </div>
            <BottomNav user={user} />
          </ClubExperienceContext.Provider>
        ) : (
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900"></div></div>}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/article" element={<BlogArticle />} />
            <Route path="*" element={<Landing />} />
          </Routes>
          </Suspense>
        )}
        <Analytics />
        <SpeedInsights />
      </div>
    </Router>
  );
}

export default App;
